import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { getProjectCountBucket, pushUserProperties, pushProjectCreated } from "@/lib/analytics";
import { logProjectError } from "@/lib/errorLogger";

export const useCreateProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (name: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("projects")
        .insert({ name, user_id: user.id })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      toast.success("Project created successfully");
      
      // Fire project_created event
      pushProjectCreated();
      
      // Push updated project_count_bucket to dataLayer
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("lifetime_project_count")
          .eq("id", user.id)
          .single();
        
        if (profile?.lifetime_project_count) {
          const bucket = getProjectCountBucket(profile.lifetime_project_count);
          if (bucket) {
            pushUserProperties({ project_count_bucket: bucket });
          }
        }
      }
    },
    onError: (error) => {
      if (error.message?.includes('project_limit_exceeded')) {
        toast.error("You've reached your plan's project limit.", {
          action: { label: 'View plans', onClick: () => { window.location.href = '/plans'; } },
        });
        return;
      }
      logProjectError('Project creation failed', error);
      toast.error("Failed to create project: " + error.message);
    },
  });
};

export const useRenameProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const { error } = await supabase
        .from("projects")
        .update({ name })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      toast.success("Project renamed successfully");
    },
    onError: (error) => {
      logProjectError('Project rename failed', error);
      toast.error("Failed to rename project: " + error.message);
    },
  });
};

export const useArchiveProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("projects")
        .update({ status: "archived" })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      toast.success("Project archived");
    },
    onError: (error) => {
      logProjectError('Project archive failed', error);
      toast.error("Failed to archive project: " + error.message);
    },
  });
};

export const useRestoreProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("projects")
        .update({ status: "active" })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      toast.success("Project restored");
    },
    onError: (error) => {
      if (error.message?.includes('project_limit_exceeded')) {
        toast.error("Restoring this project would exceed your plan's limit. Archive another project or upgrade.", {
          action: { label: 'View plans', onClick: () => { window.location.href = '/plans'; } },
        });
        return;
      }
      logProjectError('Project restore failed', error);
      toast.error("Failed to restore project: " + error.message);
    },
  });
};

export const useDeleteProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("projects")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      toast.success("Project deleted");
    },
    onError: (error) => {
      logProjectError('Project deletion failed', error);
      toast.error("Failed to delete project: " + error.message);
    },
  });
};

export const useCreateVersion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, versionNumber }: { projectId: string; versionNumber: number }) => {
      const { data, error } = await supabase
        .from("versions")
        .insert({
          project_id: projectId,
          version_number: versionNumber,
          html_content: null,
          subject: null,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["versions", variables.projectId] });
    },
    onError: (error) => {
      if (error.message?.includes('version_limit_exceeded')) {
        toast.error("Versioning is a paid feature. Upgrade to create new versions.", {
          action: { label: 'View plans', onClick: () => { window.location.href = '/plans'; } },
        });
        return;
      }
      logProjectError('Version creation failed', error);
      toast.error("Failed to create version: " + error.message);
    },
  });
};

export const useSetAwaitingNextVersion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, awaiting }: { projectId: string; awaiting: boolean }) => {
      const { error } = await supabase
        .from("projects")
        .update({ awaiting_next_version: awaiting })
        .eq("id", projectId);

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["project", variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
    onError: (error) => {
      if (error.message?.includes('version_limit_exceeded')) {
        toast.error("Versioning is a paid feature. Upgrade to create new versions.", {
          action: { label: 'View plans', onClick: () => { window.location.href = '/plans'; } },
        });
        return;
      }
      logProjectError('Set awaiting_next_version failed', error);
      toast.error("Failed to update project: " + error.message);
    },
  });
};

export const useDeleteVersion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ versionId, projectId }: { versionId: string; projectId: string }) => {
      const { error } = await supabase
        .from("versions")
        .delete()
        .eq("id", versionId);

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["versions", variables.projectId] });
    },
    onError: (error) => {
      logProjectError('Version deletion failed', error);
      toast.error("Failed to delete version: " + error.message);
    },
  });
};
